<script lang="ts" setup>
import { isImage, isVideo } from "@/modules/common";
import { useGlobalModal } from "@/stores/globalModal";
import { nextTick, ref } from "vue";

const globalModal = useGlobalModal();

// 错误状态管理
const imageLoadError = ref<Set<string>>(new Set());
const videoLoadError = ref<Set<string>>(new Set());

// 处理图片加载错误
const handleImageError = (src: string) => {
  imageLoadError.value.add(src);
};

// 处理视频加载错误
const handleVideoError = (src: string) => {
  videoLoadError.value.add(src);
};

// 重新加载图片
const reloadImage = (src: string) => {
  imageLoadError.value.delete(src);
  // 强制重新渲染图片
  let mediaList = globalModal.mediaList;
  globalModal.mediaList = [];
  nextTick(() => {
    globalModal.mediaList = mediaList;
  });
};

// 重新加载视频
const reloadVideo = (src: string) => {
  videoLoadError.value.delete(src);
  // 强制重新渲染视频
  let mediaList = globalModal.mediaList;
  globalModal.mediaList = [];
  nextTick(() => {
    globalModal.mediaList = mediaList;
  });
};

// 检查是否加载失败
const isImageLoadFailed = (src: string) => imageLoadError.value.has(src);
const isVideoLoadFailed = (src: string) => videoLoadError.value.has(src);
</script>
<template>
  <VanImagePreview
    :images="globalModal.mediaList"
    v-model:show="globalModal.showMediaPreviewModal"
    :start-position="globalModal.startPosition"
    class="media-preview"
    closeable
    showIndicators
    teleport="body"
    :close-on-click-image="false"
    @close="globalModal.showMediaPreviewModal = false"
  >
    <template #image="{ src }">
      <div
        ref="previewRef"
        class="max-w-xl my-0 mx-auto flex flex-col justify-center items-center"
      >
        <div v-if="isVideo(src)">
          <video
            controls
            style="width: 100%"
            crossorigin="anonymous"
            :data-src="src"
            @error="handleVideoError(src)"
          >
            <source :src="src" />
          </video>
        </div>
        <img
          v-else
          class="img-render-nearest min-w-40"
          :src="src"
          :key="src"
          crossorigin="anonymous"
          @error="handleImageError(src)"
        />

        <p v-if="globalModal.mediaPreviewParams.withDownloadTips">
          长按图片保存到相册
        </p>

        <!-- 图片加载失败提示 -->
        <div
          v-if="isImage(src) && isImageLoadFailed(src)"
          class="flex flex-col items-center justify-center p-8 text-center"
        >
          <button class="btn btn-primary btn-sm" @touchstart="reloadImage(src)">
            <i class="iconfont icon-refresh mr-1"></i>
          </button>
        </div>

        <!-- 视频加载失败提示 -->
        <div
          v-if="!isImage(src) && isVideoLoadFailed(src)"
          class="flex flex-col items-center justify-center p-8 text-center"
        >
          <button class="btn btn-primary btn-sm" @touchstart="reloadVideo(src)">
            <i class="iconfont icon-refresh mr-1"></i>
          </button>
        </div>
      </div>
    </template>
  </VanImagePreview>
</template>
<style>
.van-image-preview.media-preview {
  margin-top: env(safe-area-inset-top, 0);
}

.media-preview .van-image-preview__cover {
  top: unset;
  bottom: 0;
  left: 0;
  right: 0;
}

.media-preview .van-image-preview__image-wrap {
  text-align: center;
}
</style>
